/**
 * Achievements — milestones the player unlocks throughout the game.
 *
 * Each achievement has :
 *   - id            : stable identifier (used as key in unlockedAchievements)
 *   - name          : displayed title
 *   - description   : what the player did
 *   - category      : grouping label for the UI
 *   - icon          : lucide-react icon name (resolved client-side)
 *   - tier          : bronze / silver / gold / platinum (visual variant)
 *   - hint          : public hint shown BEFORE unlock (no spoiler)
 *   - check(state)  : pure predicate returning true when the player
 *                     deserves the medal. NEVER mutates state.
 *
 * The achievement engine (achievementsSlice) runs `check()` daily, once
 * an achievement fires it's marked `unlockedAt` and a Messagerie msg
 * is pushed. Subsequent ticks skip already-unlocked achievements.
 *
 * 29/05/2026 — feature request.
 */

// Helpers — keep checks self-contained and fast.
const driverCount = (s) => (s.drivers || []).length;
const supportCount = (s) => Object.keys(s.hiredSupport || {}).length;
const fleetCount = (s) => (s.vehicles || []).length;
const contractCount = (s) => (s.activeContracts || []).length;
const officeUpgradeCount = (s) => Object.keys(s.officeUpgrades || {}).length;

export const ACHIEVEMENTS = [
  // ── BUREAU (image évolutive)
  {
    id: 'office-unlocked',
    name: 'Premier siège social',
    description: 'Trois fonctions support recrutées — bienvenue dans votre vrai bureau !',
    category: 'Bureau',
    icon: 'Building2',
    tier: 'bronze',
    hint: 'Recruter 3 fonctions support',
    check: (s) => !!s.officeUnlockedAt,
  },
  {
    id: 'office-furnished',
    name: 'Bureau meublé',
    description: 'Premier équipement de bureau installé — fini la cabane du dépôt !',
    category: 'Bureau',
    icon: 'Briefcase',
    tier: 'bronze',
    hint: 'Accepter votre premier équipement de bureau',
    check: (s) => officeUpgradeCount(s) >= 1,
  },
  {
    id: 'office-enriched',
    name: 'Open-space contemporain',
    description: 'Six équipiers + 2 équipements — l\'ambiance change.',
    category: 'Bureau',
    icon: 'Coffee',
    tier: 'silver',
    hint: 'Atteindre la phase 2 du bureau',
    check: (s) => supportCount(s) >= 6 || officeUpgradeCount(s) >= 2,
  },
  {
    id: 'office-modern',
    name: 'Siège Classe moderne',
    description: '11 fonctions support OU 4 équipements — niveau premium atteint.',
    category: 'Bureau',
    icon: 'Sparkles',
    tier: 'gold',
    hint: 'Atteindre la phase 3 du bureau (Classe moderne)',
    check: (s) => supportCount(s) >= 11 || officeUpgradeCount(s) >= 4,
  },

  // ── FLOTTE
  {
    id: 'first-truck',
    name: 'Premier camion',
    description: 'Vous avez votre tout premier véhicule en main !',
    category: 'Flotte',
    icon: 'Truck',
    tier: 'bronze',
    hint: 'Acheter votre premier camion',
    check: (s) => fleetCount(s) >= 1,
  },
  {
    id: 'fleet-five',
    name: 'Petite flotte',
    description: '5 camions actifs — vous commencez à exister.',
    category: 'Flotte',
    icon: 'Truck',
    tier: 'silver',
    hint: 'Posséder 5 camions',
    check: (s) => fleetCount(s) >= 5,
  },
  {
    id: 'fleet-fifteen',
    name: 'Belle flotte',
    description: '15 camions actifs — une vraie société de transport.',
    category: 'Flotte',
    icon: 'Truck',
    tier: 'gold',
    hint: 'Posséder 15 camions',
    check: (s) => fleetCount(s) >= 15,
  },

  // ── HUMAIN
  {
    id: 'first-driver',
    name: 'Premier chauffeur',
    description: 'Un premier chauffeur dans l\'équipe — bienvenue !',
    category: 'Humain',
    icon: 'User',
    tier: 'bronze',
    hint: 'Embaucher votre premier chauffeur',
    check: (s) => driverCount(s) >= 1,
  },
  {
    id: 'team-ten',
    name: 'Équipe de 10',
    description: '10 chauffeurs CDI en poste — vraie PME.',
    category: 'Humain',
    icon: 'Users',
    tier: 'silver',
    hint: 'Avoir 10 chauffeurs',
    check: (s) => driverCount(s) >= 10,
  },
  {
    id: 'happy-team',
    name: 'Équipe au beau fixe',
    description: 'Satisfaction moyenne ≥ 85 % — votre management paie.',
    category: 'Humain',
    icon: 'Heart',
    tier: 'gold',
    hint: 'Atteindre 85 % de satisfaction moyenne sur ≥ 5 chauffeurs',
    check: (s) => {
      const d = s.drivers || [];
      if (d.length < 5) return false;
      const avg = d.reduce((acc, x) => acc + (x.satisfaction || 0), 0) / d.length;
      return avg >= 85;
    },
  },

  // ── FINANCES
  {
    id: 'first-profit',
    name: 'Première année rentable',
    description: 'Votre 1er exercice clôturé avec un bénéfice net positif.',
    category: 'Finances',
    icon: 'TrendingUp',
    tier: 'bronze',
    hint: 'Clôturer un exercice avec un résultat net positif',
    check: (s) => {
      const reports = s.yearlyReports || [];
      return reports.some((r) => (r.netResult || 0) > 0);
    },
  },
  {
    id: 'cash-100k',
    name: 'Cap des 100k €',
    description: 'Trésorerie supérieure à 100 000 € — la machine tourne.',
    category: 'Finances',
    icon: 'Wallet',
    tier: 'silver',
    hint: 'Atteindre 100 000 € de trésorerie',
    check: (s) => (s.treasury || 0) >= 100000,
  },
  {
    id: 'credit-paid',
    name: 'Premier crédit soldé',
    description: 'Vous avez fini de rembourser votre premier emprunt — bravo !',
    category: 'Finances',
    icon: 'CheckCircle',
    tier: 'silver',
    hint: 'Solder votre premier crédit',
    check: (s) => (s.totalLoansRepaid || 0) >= 1,
  },

  // ── COMMERCE
  {
    id: 'first-contract',
    name: 'Premier contrat',
    description: 'Vous avez signé votre premier client !',
    category: 'Commerce',
    icon: 'FileText',
    tier: 'bronze',
    hint: 'Signer votre premier contrat',
    check: (s) => contractCount(s) >= 1,
  },
  {
    id: 'reputation-50',
    name: 'Notoriété installée',
    description: 'Réputation 50+ — vous êtes un acteur du marché.',
    category: 'Commerce',
    icon: 'Star',
    tier: 'silver',
    hint: 'Atteindre 50 points de réputation',
    check: (s) => (s.reputation || 0) >= 50,
  },
  {
    id: 'reputation-80',
    name: 'Référence du marché',
    description: 'Réputation 80+ — les clients viennent à vous.',
    category: 'Commerce',
    icon: 'Award',
    tier: 'gold',
    hint: 'Atteindre 80 points de réputation',
    check: (s) => (s.reputation || 0) >= 80,
  },

  // ── DÉPÔTS
  {
    id: 'depot-full-hq',
    name: 'Dépôt complet',
    description: 'Les 5 zones du dépôt HQ sont activées (garage, quai, entrepôt, frigo, pompe).',
    category: 'Dépôts',
    icon: 'Warehouse',
    tier: 'gold',
    hint: 'Activer les 5 zones du dépôt principal',
    check: (s) => {
      const inv = s.activeInvestments || {};
      return inv['inv-depot-04'] && inv['inv-depot-02']
        && inv['inv-depot-05'] && inv['inv-energy-02'];
    },
  },
  {
    id: 'multi-depot',
    name: 'Réseau régional',
    description: 'Trois dépôts en activité — votre maillage prend forme.',
    category: 'Dépôts',
    icon: 'MapPin',
    tier: 'gold',
    hint: 'Avoir 3 dépôts actifs (HQ inclus)',
    check: (s) => ((s.ownedDepots || []).length + 1) >= 3,
  },

  // ── AIDES
  {
    id: 'first-aid',
    name: 'Premier soutien régional',
    description: 'Vous avez obtenu votre première aide régionale.',
    category: 'Aides',
    icon: 'Gift',
    tier: 'silver',
    hint: 'Décrocher une aide régionale au 1er janvier',
    check: (s) => (s.activeAids || []).length >= 1,
  },

  // ── PLATINE
  {
    id: 'platinum-empire',
    name: 'Empire bâti',
    description: '20+ chauffeurs, 20+ camions, 5+ contrats, réputation 80+ — un empire logistique.',
    category: 'Platine',
    icon: 'Crown',
    tier: 'platinum',
    hint: 'Bâtir un empire logistique stable',
    check: (s) =>
      driverCount(s) >= 20 &&
      fleetCount(s) >= 20 &&
      contractCount(s) >= 5 &&
      (s.reputation || 0) >= 80,
  },
];

export const ACHIEVEMENT_BY_ID = Object.fromEntries(
  ACHIEVEMENTS.map((a) => [a.id, a])
);

export const TIER_STYLES = {
  bronze:   { color: '#CD7F32', label: 'Bronze',   bg: 'from-amber-900/30 to-orange-900/20' },
  silver:   { color: '#C0C0C0', label: 'Argent',   bg: 'from-slate-600/30 to-slate-700/20' },
  gold:     { color: '#FFD700', label: 'Or',       bg: 'from-yellow-700/30 to-amber-700/20' },
  platinum: { color: '#E5E4E2', label: 'Platine',  bg: 'from-cyan-700/30 to-blue-700/20' },
};

export const CATEGORIES = [
  'Bureau', 'Flotte', 'Humain', 'Finances', 'Commerce', 'Dépôts', 'Aides', 'Platine',
];
